import { useState } from 'react';
import { api, apiRaw, enc, isBinCode } from '../api';
import { ScanField } from '../components/ScanField';
import { ErrorBox, fmtDate } from '../components/ui';
import { Icon } from '../components/Icon';
import { StepHeading } from '../components/Help';
import { useBins, useLocations } from '../hooks';

interface Result {
  at: string;
  code: string;
  ok: boolean;
  text: string;
}

type Target = { kind: 'location'; id: string; code: string } | { kind: 'bin'; id: string; code: string };

/**
 * Warehouse put-away, packing and shipping: pick a destination (a location or a bin), then scan
 * labels one after another.
 * - Destination is a location: a scanned bin moves with every lot in it; a scanned lot is taken out of its bin.
 * - Destination is a bin: scanned lots are packed into it.
 */
export function MoveLotsPage() {
  const locations = useLocations();
  const bins = useBins();
  const [target, setTarget] = useState<Target | null>(null);
  const [results, setResults] = useState<Result[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const pickByCode = async (code: string) => {
    setError(null);
    if (isBinCode(code)) {
      const { status, body } = await apiRaw(`/bins/by-code/${enc(code)}`).catch(() => ({ status: 0, body: null }));
      if (status === 200 && body.bin.active) setTarget({ kind: 'bin', id: body.bin.id, code: body.bin.code });
      else setError(new Error(status === 200 ? `Bin ${body.bin.code} is retired` : (body?.error?.message ?? 'Could not reach the server')));
      return;
    }
    const { status, body } = await apiRaw(`/locations/by-code/${enc(code)}`).catch(() => ({ status: 0, body: null }));
    if (status === 200 && body.location.active) setTarget({ kind: 'location', id: body.location.id, code: body.location.code });
    else setError(new Error(status === 404 ? `No location with code "${code}"` : status === 200 ? `Location ${code} is inactive` : 'Could not reach the server'));
  };

  const choose = (v: string) => {
    if (v.startsWith('bin:')) {
      const b = bins.data?.find((x) => x.id === v.slice(4));
      if (b) setTarget({ kind: 'bin', id: b.id, code: b.code });
    } else {
      const l = locations.data?.find((x) => x.id === v);
      if (l) setTarget({ kind: 'location', id: l.id, code: l.code });
    }
  };

  const moveCode = async (code: string) => {
    if (!target) return;
    setBusy(true);
    const at = new Date().toISOString();
    try {
      let text: string;
      if (target.kind === 'bin') {
        if (isBinCode(code)) throw new Error('A bin cannot go inside another bin. Scan LOT labels, or change the destination to a location.');
        const r = await api<{ unchanged: boolean; lotNumber: string; fromBin: string | null; lotCount: number }>(`/bins/${target.id}/pack`, { body: { lotNumber: code } });
        code = r.lotNumber;
        text = `${r.unchanged ? 'already in' : r.fromBin ? `moved from ${r.fromBin} into` : 'packed into'} ${target.code} (${r.lotCount} in bin)`;
      } else if (isBinCode(code)) {
        const r = await api<{ unchanged: boolean; binCode: string; from: string | null; to: string; lotCount: number }>('/bins/move', { body: { binCode: code, locationId: target.id } });
        code = r.binCode;
        text = `bin with ${r.lotCount} lot(s) ${r.unchanged ? `already at ${r.to}` : `${r.from ?? 'no location'} → ${r.to}`}`;
      } else {
        const r = await api<{ unchanged: boolean; from: string | null; to: string; removedFromBin: string | null }>('/lots/move', { body: { lotNumber: code, locationId: target.id } });
        text = r.unchanged ? `already at ${r.to}` : `${r.from ?? 'no location'} → ${r.to}${r.removedFromBin ? ` (taken out of ${r.removedFromBin})` : ''}`;
      }
      setResults((x) => [{ at, code, ok: true, text }, ...x]);
    } catch (e) {
      setResults((x) => [{ at, code, ok: false, text: (e as Error).message }, ...x]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="narrow">
      <h1>Move lots and bins</h1>
      <section className="card">
        <StepHeading tip="move">1. Destination: a location or a bin</StepHeading>
        {target ? (
          <div className="chosen">
            <span className={target.kind === 'bin' ? 'bin-chip' : 'loc-chip'}>
              <Icon name={target.kind === 'bin' ? 'bin' : 'pin'} /> {target.code}
            </span>
            <button className="btn btn-small btn-ghost" onClick={() => setTarget(null)}>
              Change
            </button>
          </div>
        ) : (
          <>
            <ScanField kind="location" label="LOCATION OR BIN" onScan={pickByCode} autoFocus />
            <select value="" onChange={(e) => choose(e.target.value)} aria-label="Choose location or bin">
              <option value="">…or choose a location or bin</option>
              {!!bins.data?.length && (
                <optgroup label="Bins (pack lots into it)">
                  {bins.data.map((b) => (
                    <option key={b.id} value={`bin:${b.id}`}>
                      <Icon name="bin" /> {b.code}
                      {b.locationCode ? ` (at ${b.locationCode})` : ''}
                    </option>
                  ))}
                </optgroup>
              )}
              <optgroup label="Locations">
                {locations.data?.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.code}
                  </option>
                ))}
              </optgroup>
            </select>
          </>
        )}
        <ErrorBox error={error} />
      </section>
      <section className="card">
        <h2>{target?.kind === 'bin' ? `2. Scan lot labels to pack into ${target.code}` : '2. Scan lot labels or bin labels'}</h2>
        <ScanField kind="lot" label={target?.kind === 'bin' ? 'LOT LABEL' : 'LOT OR BIN LABEL'} onScan={moveCode} busy={busy} disabled={!target} autoFocus={!!target} />
        {!target && <p className="muted small">Choose the destination first.</p>}
        {target?.kind !== 'bin' && (
          <p className="muted small">A bin label moves the bin and everything in it. A lot label moves only that lot, and takes it out of its bin.</p>
        )}
        {results.length > 0 && (
          <ul className="results">
            {results.map((r, i) => (
              <li key={i} className={r.ok ? 'ok' : 'bad'}>
                <span className="mono">{r.code}</span> <Icon name={r.ok ? 'check' : 'x'} className={r.ok ? 'ok-text' : 'error-text'} /> {r.text} <span className="muted small">{fmtDate(r.at)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
